import DataEntryHeader from "../../components/DataEntry/DataEntryHeader";
import React, {ChangeEvent, useContext, useEffect, useState} from "react";
import DataEntrySummary, {DataEntrySummaryItem} from "../../components/DataEntry/DataEntrySummary";
import {
    Button,
    CurrencyInput,
    DatePicker,
    Input,
    Label,
    PercentInput,
    RadioGroup,
    RequiredFieldsBanner,
    RequiredFieldsSubheader
} from "../../components";
import {MemberGroup, MemberGroupMember} from "../../ClientManagement/models/InvestorGroupType";
import {useHistory, useParams} from "react-router-dom";
import {RouteWithId} from "../../routes/types";
import useProfileAndProposals from "../../hooks/useProfileAndProposals";
import {EquityCompensationFormData} from "../models/EquityCompensation";
import {DISPLAY_DATE_FORMAT, ISO8601_DATE_FORMAT} from "../../constants/common";
import {booleanToRadioButton, radioButtonToBoolean} from "../formHelpers";
import {RadioYesNoOptional} from "../models/Common";
import moment, {Moment} from "moment";
import deepEquals from "fast-deep-equal";
import AssetsViewContext from "../common/AssetsViewContext";
import {formatCurrencyRounded} from "src/utils/format";
import {assetsApiClient} from "../AssetsApiClient";
import {setActiveFormAsset} from "../clientAssetsSlice";
import {useAppDispatch} from "../../store/hooks";
import {calculateEquityCompensationValue} from "../AssetSummary/common/AssetSummaryCalculator";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {HistoryBlockModal} from "../../components/HistoryBlockModal/HistoryBlockModal";
import OwnerDropdown from "../GeneralInflow/GeneralInflowForm/OwnerDropdown";
import {LifeStatus} from "../../ClientManagement/models/MemberType";

export type EquityCompensationProps = {
    handleSave: (request: EquityCompensationFormData) => Promise<EquityCompensationFormData>,
    initialEquityComp: EquityCompensationFormData,
    handleCancel: (isFormChanged: boolean) => void,
    memberGroup: MemberGroup,
    formatTitle: (description: string) => string,
}

export function EquityCompensationForm(props: EquityCompensationProps) {
    const {id} = useParams<RouteWithId>();
    const {profile} = useProfileAndProposals(id );
    const history = useHistory();
    const [isRequiredFieldsBannerShown, setRequiredFieldsBannerShown] = useState(false);
    const [equityCompensationFormData, setEquityCompensationFormData] = useState(props.initialEquityComp);
    const [isAsOfDateInlineErrorShown, setIsAsOfDateInlineErrorShown] = useState(false);
    const [isStockNameInlineErrorShown, setIsStockNameInlineErrorShown] = useState(false);
    const [isAccountNameInlineErrorShown, setIsAccountNameInlineErrorShown] = useState(false);
    const viewType = useContext(AssetsViewContext);
    const dispatch = useAppDispatch();
    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const [showNavigationModal, setShowNavigationModal] = useState(true);

    const isAnyRequiredFieldEmpty = () => {
        const {entityOrAccountName, companyOrStockName, asOfDate} = equityCompensationFormData;
        return !entityOrAccountName || !companyOrStockName || !asOfDate;
    }

    useEffect(() => {
        setRequiredFieldsBannerShown(isRequiredFieldsBannerShown && isAnyRequiredFieldEmpty());
    }, [equityCompensationFormData.entityOrAccountName, equityCompensationFormData.companyOrStockName, equityCompensationFormData.asOfDate]);

    useEffect(() => {
        dispatch(setActiveFormAsset({
            assetType: 'equityCompensation',
            id: equityCompensationFormData.id,
            inEstateValue: calculateEquityCompensationValue(equityCompensationFormData, viewType),
            description: equityCompensationFormData.entityOrAccountName,
            hasInEstateOwnership: true,
        }));
        return clearActiveFormAsset;
    }, [equityCompensationFormData.entityOrAccountName]);

    const clearActiveFormAsset = () => {
        dispatch(setActiveFormAsset(null));
    }

    const getMemberName = (member: MemberGroupMember): string => {
        return `${member.firstName} ${member.lastName}`;
    }

    const summaryItems: DataEntrySummaryItem[] = [
        {
            label: 'Entity / Account Name',
            value: equityCompensationFormData.entityOrAccountName
        },
        {
            label: 'Owner',
            value: getMemberName(getCurrentOwnerByOwnerMemberId(equityCompensationFormData.owner))
        },
        {
            label: 'Pre-Tax Unvested',
            value: equityCompensationFormData.preTaxUnvestedValue ?
                formatCurrencyRounded(equityCompensationFormData.preTaxUnvestedValue, 0) : ''
        },
        {
            label: 'Pre-Tax Vested',
            value: equityCompensationFormData.preTaxVestedValue ?
                formatCurrencyRounded(equityCompensationFormData.preTaxVestedValue, 0) : ''
        },
        {
            label: 'After-Tax Unvested',
            value: equityCompensationFormData.afterTaxUnvestedValue ?
                formatCurrencyRounded(equityCompensationFormData.afterTaxUnvestedValue, 0) : ''
        },
        {
            label: 'After-Tax Vested',
            value: equityCompensationFormData.afterTaxVestedValue ?
                formatCurrencyRounded(equityCompensationFormData.afterTaxVestedValue, 0) : ''
        }
    ];

    const handleTaxRateChange = (value: string) => {
        if (!value) value = "0";
        setEquityCompensationFormData({
            ...equityCompensationFormData, taxRate: value
        })
    }

    const calculateSummary = () => {
        assetsApiClient.calculateEquityCompensationSummary(profile.id, equityCompensationFormData).then(res => {
            setEquityCompensationFormData(res);
            dispatch(setActiveFormAsset({
                assetType: 'equityCompensation',
                id: equityCompensationFormData.id,
                inEstateValue: calculateEquityCompensationValue(res, viewType),
                description: res.entityOrAccountName,
                hasInEstateOwnership: true,
            }));
        });
    }

    const navigateToAssetsView = () => {
        history.push(`/Profile/${profile.id}/ClientProfile/${viewType}`);
    }

    const handleSave = async () => {
        const {entityOrAccountName, companyOrStockName, asOfDate} = equityCompensationFormData;
        if (!entityOrAccountName || !companyOrStockName || !asOfDate) {
            setRequiredFieldsBannerShown(true);
            setIsAccountNameInlineErrorShown(!entityOrAccountName);
            setIsStockNameInlineErrorShown(!companyOrStockName);
            setIsAsOfDateInlineErrorShown(!asOfDate);
            return false;
        }
        await props.handleSave(equityCompensationFormData);
        return true;
    }

    const handleSaveButton = async (isNavigateOptionAndGrant: boolean = false) => {
        const {entityOrAccountName, companyOrStockName, asOfDate} = equityCompensationFormData;
        if (!entityOrAccountName || !companyOrStockName || !asOfDate) {
            setRequiredFieldsBannerShown(true);
            setIsAccountNameInlineErrorShown(!entityOrAccountName);
            setIsStockNameInlineErrorShown(!companyOrStockName);
            setIsAsOfDateInlineErrorShown(!asOfDate);
            return;
        }
        setShowNavigationModal(false);
        const equityData = await props.handleSave(equityCompensationFormData);
        if (isNavigateOptionAndGrant) {
            history.push(`/Profile/${profile.id}/ClientProfile/${viewType}/EquityCompensation/${equityData.id}/AddOptionsAndGrants`);
        } else {
            navigateToAssetsView()
        }
    }

    const isFormChanged = !deepEquals(equityCompensationFormData, props.initialEquityComp);

    const handleCancelButton = () => {
        setShowNavigationModal(false);
        props.handleCancel(isFormChanged);
    }

    function getCurrentOwnerByOwnerMemberId(ownerMemberId: string) {
        let selectedOwner = props.memberGroup.primaryMember;
        if (props.memberGroup.partnerMember?.id === ownerMemberId) {
            selectedOwner = props.memberGroup.partnerMember;
        } else if (props.memberGroup.additionalMembers.length > 0 && (props.memberGroup.additionalMembers.find(member => member.id === ownerMemberId) !== undefined)) {
            selectedOwner = props.memberGroup.additionalMembers.find(member => member.id === ownerMemberId)!
        }
        return selectedOwner;
    }

    const isFutureDate = (date: Moment) => date.isAfter(moment().endOf('day'));

    const handleAddStockOptionAndGrants = async () => {
        await handleSaveButton(true);
    }

    const onOwnerChange = (ownerId: string) => {
        setEquityCompensationFormData({
            ...equityCompensationFormData,
            owner: ownerId,
        })
    }
    const revTrustIdSuffix = ':revTrust';
    const jointId = 'joint';

    const getMemberOption = (member: MemberGroupMember) => {
        const age = member.age;
        const name = member.firstName + " " + member.lastName;
        return {label: `${name} (Age ${age})`, value: member.id};
    }
    const getRevocableTrustOption = (member: MemberGroupMember) => {
        return {label: `${member.firstName} Revocable Trust`, value: `${member.id}${revTrustIdSuffix}`};
    };

    const ownerOptions = [
        getMemberOption(props.memberGroup.primaryMember),
        getRevocableTrustOption(props.memberGroup.primaryMember)
    ];

    if (props.memberGroup.partnerMember && props.memberGroup.partnerMember.lifeStatus === LifeStatus.Living) {
        ownerOptions.push(getMemberOption(props.memberGroup.partnerMember))
        ownerOptions.push(getRevocableTrustOption(props.memberGroup.partnerMember))
        ownerOptions.push({label: 'Joint', value: jointId});
    }

    return (
        <aside aria-label="Equity Compensation" className="equity-compensation-form-section">
            <HistoryBlockModal
                when={isFormChanged && showNavigationModal}
                itemType={'page'}
                onSave={handleSave}
            />
            <DataEntryHeader
                className='dataEntryHeader'
                title={props.formatTitle(equityCompensationFormData.entityOrAccountName)}
                SubtitleComponent={RequiredFieldsSubheader}
                onPrimaryButtonClick={() => handleSaveButton(false)}
                disablePrimaryButton={isProfileWithProposalsOrArchived}
                onSecondaryButtonClick={handleCancelButton}
                primaryButtonText="Save"
                secondaryButtonText="Cancel"
            />
            <RequiredFieldsBanner showAlert={isRequiredFieldsBannerShown} itemType="Equity compensation"/>
            <div className="equity-compensation__form layout-data-entry-form">
                <article>
                    <div className="layout-data-entry-form__field">
                        <Label label="Entity / Account Name" htmlFor="entityAndAccountNameId" required/>
                        <Input
                            id="entityAndAccountNameId"
                            className="entityAndAccountName_field"
                            name="accountName"
                            size="medium"
                            value={equityCompensationFormData.entityOrAccountName}
                            readOnly={isProfileWithProposalsOrArchived}
                            onChange={(event) => {
                                setEquityCompensationFormData({
                                    ...equityCompensationFormData,
                                    entityOrAccountName: event.target.value
                                })
                                setIsAccountNameInlineErrorShown(!event.target.value);
                            }}
                            error={isAccountNameInlineErrorShown ? "Entity / Account Name is required." : undefined}
                        />
                    </div>
                    <div className="layout-data-entry-form__field">
                        <Label label="Ticker" htmlFor="tickerId"/>
                        <Input
                            id="tickerId"
                            className="ticker-input_field"
                            name="Ticker"
                            size="medium"
                            value={equityCompensationFormData.ticker}
                            readOnly={isProfileWithProposalsOrArchived}
                            onChange={(event) => {
                                setEquityCompensationFormData({
                                    ...equityCompensationFormData,
                                    ticker: event.target.value
                                })
                            }}
                        />
                    </div>
                    <div className="layout-data-entry-form__field as-of-date-picker-comp">
                        <Label label="As of Date" htmlFor="asOfDateId" required/>
                        <div style={{position: "relative"}}>
                            <DatePicker
                                className="equity-compensation-asofdate"
                                id="asOfDateId"
                                displayFormat={DISPLAY_DATE_FORMAT}
                                hideKeyboardShortcutsPanel
                                isOutsideRange={isFutureDate}
                                disabled={isProfileWithProposalsOrArchived}
                                date={equityCompensationFormData.asOfDate ? moment(equityCompensationFormData.asOfDate) : undefined}
                                onDateChange={
                                    (date: Moment) => {
                                        const selectedDate = date?.utc().format(ISO8601_DATE_FORMAT);
                                        setEquityCompensationFormData({
                                            ...equityCompensationFormData,
                                            asOfDate: selectedDate
                                        })
                                        setIsAsOfDateInlineErrorShown(!selectedDate);
                                    }
                                }
                                error={isAsOfDateInlineErrorShown ? "Invalid date format" : undefined}
                            />
                        </div>
                    </div>
                    <div className="layout-data-entry-form__field">
                        <Label label="Company / Stock Name" htmlFor="compStockId" required/>
                        <Input
                            id="compStockId"
                            name="StockName"
                            size="medium"
                            value={equityCompensationFormData.companyOrStockName}
                            readOnly={isProfileWithProposalsOrArchived}
                            onChange={(event) => {
                                setEquityCompensationFormData({
                                    ...equityCompensationFormData,
                                    companyOrStockName: event.target.value
                                })
                                setIsStockNameInlineErrorShown(!event.target.value);
                            }}
                            error={isStockNameInlineErrorShown ? "Company / Stock Name is required." : undefined}
                        />
                    </div>
                    <div className="layout-data-entry-form__field">
                        <Label label="Stock Price" htmlFor="stockPriceId"/>
                        <CurrencyInput
                            id="stockPriceId"
                            className="stockprice_field"
                            name="stockPrice"
                            size="medium"
                            allowDecimals={true}
                            formatOptions={{
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                                style: 'decimal',
                            }}
                            value={equityCompensationFormData.stockPrice === null ? 0 : equityCompensationFormData.stockPrice}
                            readOnly={isProfileWithProposalsOrArchived}
                            onChangeValue={(_, newValue) => {
                                if (!newValue) newValue = 0;
                                setEquityCompensationFormData({
                                    ...equityCompensationFormData,
                                    stockPrice: newValue.toString(),
                                });
                            }}
                            onBlur={calculateSummary}
                        />
                    </div>
                    <div className="layout-data-entry-form__field">
                        <PercentInput
                            defaultValue={"0"}
                            value={equityCompensationFormData.taxRate.toString()}
                            label="Tax Rate"
                            size="medium"
                            minValue={0}
                            maxValue={99}
                            onChange={handleTaxRateChange}
                            onBlur={calculateSummary}
                            disabled={isProfileWithProposalsOrArchived}
                        />
                    </div>
                    <OwnerDropdown owner={equityCompensationFormData.owner}
                                   owners={ownerOptions}
                                   onChange={onOwnerChange}
                                   disabled={isProfileWithProposalsOrArchived}
                    />
                    <div className="layout-data-entry-form__field">
                        <RadioGroup
                            id="doesPermitBeneficiary"
                            name="doesPermitBeneficiary"
                            label="Does this account permit a beneficiary?"
                            layout="horizontal"
                            values={["Yes", "No"]}
                            disabled={isProfileWithProposalsOrArchived}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const {value} = e.target;
                                setEquityCompensationFormData({
                                    ...equityCompensationFormData,
                                    doesPermitBeneficiary: radioButtonToBoolean(value as RadioYesNoOptional),
                                })
                            }}
                            selected={booleanToRadioButton(equityCompensationFormData.doesPermitBeneficiary)}
                        />
                    </div>
                </article>
                <aside aria-label="Account Summary">
                    <DataEntrySummary
                        items={summaryItems}
                        title="Account Summary"
                    >
                        <Button
                            ariaLabel="Add Options and Grants"
                            size="medium"
                            icon="left"
                            iconName="chart_bar"
                            onClick={handleAddStockOptionAndGrants}
                        >
                            Add Options and Grants
                        </Button>
                    </DataEntrySummary>
                </aside>
            </div>
        </aside>
    );
}